/**
 * Utilitários para baixar uma transcrição revisada nos formatos JSON, DOCX
 * (documento Word) e PDF.
 *
 * Por opção arquitetural NÃO introduzimos novas dependências (jspdf, docx,
 * file-saver, etc.) — todas as variantes são geradas no cliente usando
 * APIs nativas do navegador:
 *
 *   - JSON  : `Blob` + `URL.createObjectURL` + `<a download>`.
 *   - DOCX  : também gerado como blob, mas usando o truque consagrado de
 *             produzir HTML com cabeçalho MIME do Word (application/msword).
 *             O Word/LibreOffice abrem normalmente; a extensão `.doc`
 *             (em vez de `.docx`) reflete exatamente o conteúdo gerado.
 *   - PDF   : abrimos uma nova janela com o HTML formatado e disparamos
 *             `window.print()` automaticamente; o usuário escolhe "Salvar
 *             como PDF" no diálogo nativo de impressão. A janela se fecha
 *             depois que o usuário fecha o diálogo.
 *
 * O HTML gerado é o mesmo para DOCX e PDF (apenas o cabeçalho/Content-Type
 * muda) — assim a aparência impressa fica consistente entre os formatos.
 */

import { formatHHMMSS } from "./transcriptionFormat";
import {
  getDisplayName,
  getDisplayRole,
} from "./speakerHelpers";

/* Texto utilizado para "speaker desconhecido" no HTML/DOCX. Mantemos em
   português porque o download é apenas um snapshot textual; o restante da
   UI já cobre a internacionalização do app. Se for necessário traduzir o
   conteúdo do download no futuro, este label pode ser passado por
   parâmetro pelos consumidores. */
const UNIDENTIFIED_LABEL = "Não identificado";

/**
 * Escapa caracteres reservados de HTML para evitar quebras / injeção
 * indireta no documento gerado.
 */
function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Sanitiza um nome de arquivo para uso em `<a download>`. Substitui
 * caracteres problemáticos por `_` e força um sufixo válido.
 */
function buildDownloadName(baseName, extension) {
  const safe = String(baseName || "transcricao")
    .replace(/[\\/:*?"<>|]+/g, "_")
    .replace(/\.[a-z0-9]{1,4}$/i, "")
    .trim() || "transcricao";
  return `${safe}.${extension}`;
}

/**
 * Dispara o download de um Blob com o nome solicitado. Cria e remove o
 * `<a>` de forma transparente para o consumidor.
 *
 * Exportado também como `saveBlobAsFile` para uso a partir de chamadas
 * autenticadas ao backend (que devolvem `{ blob, filename }`) — assim
 * mantemos um único ponto que sabe disparar o download no DOM.
 */
export function saveBlobAsFile(blob, filename) {
  triggerBlobDownload(blob, filename);
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  /* O delay garante que o download foi disparado antes de soltar a URL. */
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Gera o HTML "renderizado" da transcrição, em um layout amigável para
 * leitura/impressão. O mesmo HTML é usado tanto pela versão DOCX quanto
 * pela impressão (PDF), variando apenas pelo cabeçalho do arquivo.
 */
function buildTranscriptionHtml({ transcription, filename, metadata }) {
  const speakers = transcription?.speakers || [];
  const sentences = transcription?.sentences || [];

  const speakersById = new Map();
  speakers.forEach((sp) => {
    if (sp?.speaker_id) speakersById.set(sp.speaker_id, sp);
  });

  const speakerHeaderRows = speakers
    .map((sp) => {
      const name = escapeHtml(getDisplayName(sp, UNIDENTIFIED_LABEL));
      const role = escapeHtml(getDisplayRole(sp) || "");
      const roleHtml = role ? ` <span class="speaker-role">— ${role}</span>` : "";
      return `<li><strong>${name}</strong>${roleHtml}</li>`;
    })
    .join("");

  const sentenceRows = sentences
    .map((s) => {
      const speaker = speakersById.get(s?.speaker_id);
      const speakerName = escapeHtml(getDisplayName(speaker, UNIDENTIFIED_LABEL));
      const time = escapeHtml(formatHHMMSS(s?.start || 0));
      const text = escapeHtml(s?.text || "");
      return `
<tr class="sentence">
  <td class="time">${time}</td>
  <td class="speaker"><strong>${speakerName}</strong></td>
  <td class="text">${text}</td>
</tr>`;
    })
    .join("");

  /* Nome do aprovador no documento exportado. Preferimos o novo campo
     `approved_by_username` (vindo do backend); caímos para
     `approved_by_name` (chave legada eventualmente em payloads antigos).
     Quando temos a data mas não o nome, ainda exibimos a linha. */
  const approverName =
    metadata?.approved_by_username || metadata?.approved_by_name || null;
  const approvalLine = metadata?.approval_timestamp
    ? approverName
      ? `<p class="meta">Aprovada por <strong>${escapeHtml(approverName)}</strong> em ${escapeHtml(metadata.approval_timestamp)}.</p>`
      : `<p class="meta">Aprovada em ${escapeHtml(metadata.approval_timestamp)}.</p>`
    : "";

  return `
<style>
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #1f2937; margin: 24px; }
  h1 { font-size: 18pt; margin-bottom: 4px; }
  h2 { font-size: 12pt; margin-top: 24px; margin-bottom: 8px; color: #374151; }
  ul.speakers { padding-left: 18px; margin-top: 4px; }
  ul.speakers li { margin-bottom: 2px; }
  .speaker-role { color: #6b7280; font-weight: normal; }
  .meta { color: #6b7280; font-size: 10pt; margin: 0 0 6px 0; }
  table.sentences { width: 100%; border-collapse: collapse; margin-top: 8px; }
  table.sentences td { vertical-align: top; padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 11pt; }
  td.time { width: 80px; font-family: 'Courier New', monospace; color: #6b7280; }
  td.speaker { width: 180px; }
  td.text { line-height: 1.45; }
</style>
<h1>${escapeHtml(filename || "Transcrição")}</h1>
${approvalLine}
${speakers.length > 0 ? `<h2>Participantes</h2><ul class="speakers">${speakerHeaderRows}</ul>` : ""}
<h2>Transcrição</h2>
<table class="sentences"><tbody>${sentenceRows}</tbody></table>
`;
}

/**
 * Baixa o conteúdo da transcrição como JSON pretty-printed.
 *
 * @param {object} params
 * @param {object} params.transcription
 * @param {string} params.filename       nome original do arquivo de mídia
 * @param {object} [params.metadata]     metadata opcional. Suporta as chaves:
 *   - `approved_by_username` (preferido, novo nome do backend)
 *   - `approved_by_name`     (legado — mantido por compatibilidade)
 *   - `approval_timestamp`, `last_saved_timestamp`
 */
export function downloadTranscriptionAsJson({
  transcription,
  filename,
  metadata,
}) {
  const payload = {
    filename,
    metadata: metadata || null,
    transcription: transcription || { speakers: [], sentences: [] },
  };
  const json = JSON.stringify(payload, null, 2);
  const blob = new Blob([json], { type: "application/json;charset=utf-8" });
  triggerBlobDownload(blob, buildDownloadName(filename, "json"));
}

/**
 * Baixa o conteúdo da transcrição como documento Word.
 *
 * Implementação: gera um HTML com o cabeçalho MIME do Word
 * (`application/msword`) e extensão `.doc`. Tanto o Word quanto o
 * LibreOffice abrem normalmente. Não precisamos da estrutura completa de
 * `.docx` (que seria um zip de XML) porque o cabeçalho `<html>` com o
 * Content-Type adequado é suficiente para a maioria dos casos de uso.
 */
export function downloadTranscriptionAsDocx({
  transcription,
  filename,
  metadata,
}) {
  const body = buildTranscriptionHtml({ transcription, filename, metadata });
  const html = `<!DOCTYPE html>
<html xmlns:o='urn:schemas-microsoft-com:office:office'
      xmlns:w='urn:schemas-microsoft-com:office:word'
      xmlns='http://www.w3.org/TR/REC-html40'>
<head>
<meta charset='utf-8'>
<title>${escapeHtml(filename || "Transcrição")}</title>
<xml>
  <w:WordDocument>
    <w:View>Print</w:View>
    <w:Zoom>100</w:Zoom>
    <w:DoNotOptimizeForBrowser/>
  </w:WordDocument>
</xml>
</head>
<body>${body}</body>
</html>`;
  const blob = new Blob(["\ufeff", html], {
    type: "application/msword;charset=utf-8",
  });
  /* Mantemos a extensão `.doc` porque o conteúdo é Word-HTML, não OOXML
     (`.docx`). Word, LibreOffice e Pages reconhecem corretamente. */
  triggerBlobDownload(blob, buildDownloadName(filename, "doc"));
}

/**
 * Abre uma nova janela com o HTML da transcrição e dispara o diálogo de
 * impressão automaticamente. O usuário escolhe "Salvar como PDF" no
 * diálogo nativo do navegador.
 *
 * Limitações: precisa de pop-ups habilitados e não devolve o PDF como
 * `Blob`. Em troca evita uma dependência pesada (jspdf/pdfkit). Quando o
 * backend disponibilizar um endpoint dedicado, esta função pode ser
 * trocada por um simples `fetch` + download.
 */
export function downloadTranscriptionAsPdf({
  transcription,
  filename,
  metadata,
}) {
  const body = buildTranscriptionHtml({ transcription, filename, metadata });
  const html = `<!DOCTYPE html>
<html><head>
<meta charset='utf-8'>
<title>${escapeHtml(filename || "Transcrição")}</title>
</head><body>${body}
<script>
  /* window.onload garante que estilos foram aplicados antes do print.
     Apos o print, fechamos automaticamente a janela auxiliar. */
  window.onload = function() {
    window.focus();
    window.print();
    window.onafterprint = function() { setTimeout(function(){ window.close(); }, 100); };
  };
</script>
</body></html>`;
  const printWindow = window.open("", "_blank", "noopener,noreferrer");
  if (!printWindow) {
    /* Pop-up bloqueado: caímos para uma estratégia menos amigável (download
       do HTML) só para que o usuário não fique sem nada. */
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    triggerBlobDownload(blob, buildDownloadName(filename, "html"));
    return;
  }
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
}
